import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation } from "@angular/core";
import { Select } from "@ngxs/store";
import startOfDay from "date-fns/startOfDay";
import subDays from "date-fns/subDays";
import equal from "fast-deep-equal";
import { Observable, Subscription, combineLatest } from "rxjs";
import { filter } from "rxjs/operators";
import { METERS_PER_KILOMETER, METERS_PER_MILE } from "src/app/constants/distance-constants";
import { translateAndFormat } from "src/app/i18next";
import { Asset } from "src/app/models/core-api.models";
import { InspectionTypeSummary } from "src/app/models/inspection-type-summary.model";
import { Inspector } from "src/app/models/inspector.model";
import { LanguageDictionary } from "src/app/models/language-dictionary.model";
import { EvirDivision } from "src/app/models/shared.model";
import { ZoneLayoutSummary } from "src/app/models/zone-layout-summary.model";
import { LanguageDictionaryService } from "src/app/services/language-dictionary/language-dictionary.service";
import { LocaleService } from "src/app/services/locale/locale.service";
import { MemCacheService } from "src/app/services/mem-cache/mem-cache.service";
import { arraysEquivalent } from "src/utils/arraysEquivalent/arraysEquivalent";
import { groupOptionsByStrippedLabel } from "src/utils/groupOptionsByStrippedLabel/groupOptionsByStrippedLabel";
import { isDefined } from "src/utils/isDefined/isDefined";
import { isZonarTheme } from "src/utils/isZonarTheme/isZonarTheme";
import { newDate } from "src/utils/newDate/newDate";
import { urlSearchParamsReplacer } from "src/utils/urlSearchParamsReplacer/urlSearchParamsReplacer";
import { AppState } from "../../app.state";
import { DateRangeFilterChange, DefectTypeOption, FilterCardQueryStrings } from "../../models/emitter-events.models";
import { GetTableFilterComponent, TableFilterChip, TableRecord } from "../get-table-filter/get-table-filter.component";
import { tableFilterChipsEqual } from "../get-table-filter/helpers/table-filter-chips-equal";
import { MultiSelectDropdownComponent, Option } from "../multi-select-dropdown/multi-select-dropdown.component";
import { SingleOption } from "../single-select-dropdown/single-select-dropdown.component";

export class EnabledFilters {
	assetId: boolean;
	assetType: boolean;
	customDateRange: boolean;
	dateRange: boolean;
	defectType: boolean;
	inspectionType: boolean;
	homeLocation: boolean;
	inspector: boolean;
	page: string;
	severity: boolean;
	tableFilter: boolean;
	resetFilterButton: boolean;
	searchBar: boolean;
	inspectionStatus: boolean;
	tripDuration?: boolean;
	distanceTraveled?: boolean;
}

function getIdFromRecord(record: TableRecord, idFieldName: string) {
	return record.fields.find(field => field.name === idFieldName).value;
}

// state
export interface FilterCardState {
	currentQueryStrings: FilterCardQueryStrings;
	tableFilterChips: Array<TableFilterChip>;
}

@Component({
	selector: "app-filter-card",
	templateUrl: "./filter-card.component.html",
	styleUrls: ["./filter-card.component.scss"],
	encapsulation: ViewEncapsulation.None,
})
export class FilterCardComponent implements OnInit, OnDestroy {
	@ViewChild("getTableFilter") getTableFilterComponent: GetTableFilterComponent;
	@ViewChild(MultiSelectDropdownComponent) multiSelectDropdownComponent: MultiSelectDropdownComponent;

	filtersActive = false;
	state: FilterCardState;
	stateKey: string;
	subscriptions = new Subscription();

	@Input() context = "inspection-history";
	@Input() enabledFilters: EnabledFilters;
	@Output() filterQueryObject = new EventEmitter<FilterCardQueryStrings>();

	@Select(AppState.selectAssets) assets$: Observable<Asset[]>;
	@Select(AppState.selectAssetTypes) assetTypes$: Observable<ZoneLayoutSummary[]>;
	@Select(AppState.selectDivisions) divisions$: Observable<EvirDivision[]>;
	@Select(AppState.selectInspectionTypes) inspectionTypes$: Observable<InspectionTypeSummary[]>;
	@Select(AppState.selectInspectors) inspectors$: Observable<Inspector[]>;
	@Select(AppState.selectLanguageDictionary) languageDictionary$: Observable<LanguageDictionary>;

	public selectedDivisionId = "";

	public filterCardClass = "mat-card-raised";
	public defaultStartDate = startOfDay(subDays(newDate(), 7));
	public defaultEndDate = newDate();
	public currentStartDate = this.defaultStartDate;
	public currentEndDate: Date = this.defaultEndDate;
	public inspectionTypeLabel = translateAndFormat("inspection type", "capitalize");
	public inspectionTypeContext: string;
	public inspectionTypes: Array<Option> = [
		{
			label: translateAndFormat("loading", "capitalize"),
			value: "asdfasdofu0987a98sd9f76a9sd7f",
		},
	]; // Inspection type filter vars
	public assetIds: Array<Option> = [
		{
			label: translateAndFormat("loading", "capitalize"),
			value: "asdfasdofu0987a98sd9f76a9sd7f",
		},
	]; // Asset id filter vars
	public assetIdLabel = translateAndFormat("asset id", "capitalize");
	public assetIdContext: string;
	public assetTypes: Array<Option> = [
		{
			label: translateAndFormat("loading", "capitalize"),
			value: "asdfasdofu0987a98sd9f76a9sd7f",
		},
	]; // Asset type filter vars
	public assetTypeLabel = translateAndFormat("asset type", "capitalize");
	public assetTypeContext: string;
	public homeLocations: Array<Option> = [
		{
			label: translateAndFormat("loading", "capitalize"),
			value: "asdfasdofu0987a98sd9f76a9sd7f",
		},
	]; // Home location filter vars
	public homeLocationLabel = translateAndFormat("home location", "capitalize");

	// Trip duration filter vars
	public tripDurationLabel = translateAndFormat("trip duration", "capitalize");
	// Distance traveled filter vars
	public distanceTraveledLabel = translateAndFormat("distance traveled", "capitalize");

	public distances: Array<SingleOption> = [
		{
			label: translateAndFormat("loading", "capitalize"),
			value: "asdfasdofu0987a98sd9f76a9sd7f",
		},
	]; // Distance filter vars

	public homeLocationContext: string;
	public tripDurationContext: string;
	public distanceTraveledContext: string;
	public inspectors: Array<Option> = [
		{
			label: translateAndFormat("loading", "capitalize"),
			value: "asdfasdofu0987a98sd9f76a9sd7f",
		},
	]; // Inspector filter vars

	public inspectorLabel = translateAndFormat("inspector", "capitalize");
	public inspectorContext: string;
	public severities: Array<Option> = []; // Severity filter
	public severityLabel = translateAndFormat("severity", "capitalize");
	public severityContext: string;
	public inspectionStatusLabel = translateAndFormat("inspection status", "capitalize");
	public inspectionStatusContext: string;
	public inspectionStatusOptions: Array<Option> = []; // Inspection status filter vars
	public currentQueryStrings: FilterCardQueryStrings = {
		dates: [{ startTime: this.defaultStartDate, endTime: this.defaultEndDate }],
	};
	public tableFilterChips: Array<TableFilterChip> = [];
	public defectTypes: DefectTypeOption[] = [];
	public isZonarTheme = isZonarTheme();
	public resetCheckbox = false;

	private readonly MAX_DISTANCE_TRAVEL_MILES = 15; // Maximum distance travel in miles
	private readonly MAX_DISTANCE_TRAVEL_KILOMETERS = 25; // Roughly the same reach as 15 miles

	constructor(
		public memCacheService: MemCacheService,
		private languageDictionaryService: LanguageDictionaryService,
		private localeService: LocaleService,
	) {}

	ngOnDestroy() {
		this.subscriptions.unsubscribe();
	}

	ngOnInit() {
		this.stateKey = `filter-card: ${this.context}`;

		// Custom Date filter init
		if (this.enabledFilters.dateRange || this.enabledFilters.customDateRange) {
			this.addQueryString("dates", [{ startTime: this.defaultStartDate, endTime: this.defaultEndDate }]);
		}

		// inspection type filter init
		if (this.enabledFilters.inspectionType) {
			this.inspectionTypeContext = `inspectionType-filter: ${this.context}`;
			this.subscriptions.add(
				combineLatest([this.inspectionTypes$, this.languageDictionary$])
					.pipe(filter(([inspectionTypes, langDict]) => isDefined(inspectionTypes) && isDefined(langDict)))
					.subscribe(([inspectionTypes]) => {
						// Group inspection types whose translated label is identical once the
						// ":<build/version>" suffix is stripped. Each grouped option carries a
						// comma-joined list of every inspectionTypeId it represents; the handler
						// splits that back out so the backend receives all underlying ids.
						this.inspectionTypes = groupOptionsByStrippedLabel(
							inspectionTypes.map(({ inspectionTypeId, name, langId }) => ({
								id: inspectionTypeId,
								translation: this.languageDictionaryService.getTranslation(langId) || name,
							})),
						);
					}),
			);
		}

		// asset id filter init
		if (this.enabledFilters.assetId) {
			this.assetIdContext = `asset-id-filter: ${this.context}`;
			this.assets$.pipe(filter(isDefined)).subscribe(assets => {
				this.assetIds = assets.map(({ id, name }) => ({
					value: id,
					label: name,
				}));
			});
		}

		// asset type filter init
		if (this.enabledFilters.assetType) {
			this.assetTypeContext = `assetType-filter: ${this.context}`;
			this.subscriptions.add(
				combineLatest([this.assetTypes$, this.languageDictionary$])
					.pipe(filter(([assetTypes, langDict]) => isDefined(assetTypes) && isDefined(langDict)))
					.subscribe(([assetTypes]) => {
						// Group asset types whose translated label is identical once the
						// ":<build/version>" suffix is stripped. Each grouped option carries a
						// comma-joined list of every zone layout id it represents; the handler
						// splits that back out so the backend receives all underlying ids.
						this.assetTypes = groupOptionsByStrippedLabel(
							assetTypes.map(({ layoutId, name, langId }) => ({
								id: layoutId,
								translation: this.languageDictionaryService.getTranslation(langId) || name,
							})),
						);
					}),
			);
		}

		// home location filter init
		if (this.enabledFilters.homeLocation) {
			this.homeLocationContext = `home-location-filter: ${this.context}`;
			this.divisions$.pipe(filter(isDefined)).subscribe(divisions => {
				this.homeLocations = divisions.map(({ divisionId, divisionName }) => ({
					value: divisionId,
					label: divisionName,
				}));
			});
		}

		// trip duration  filter init
		if (this.enabledFilters.tripDuration) {
			this.tripDurationContext = `trip-duration-filter: ${this.context}`;
		}

		// distance traveled filter init
		if (this.enabledFilters.distanceTraveled) {
			this.distanceTraveledContext = `distance-traveled-filter: ${this.context}`;
			this.distances = this.buildDistanceOptions();
		}

		// inspector filter init
		this.inspectorContext = `inspector-filter: ${this.context}`;
		this.inspectors$.pipe(filter(isDefined)).subscribe(inspectors => {
			this.inspectors = inspectors.map(({ inspectorId, inspectorFirstName, inspectorLastName }) => ({
				value: inspectorId,
				label: inspectorFirstName + " " + inspectorLastName,
			}));
		});

		if (this.enabledFilters.severity) {
			this.severityContext = `severity-filter: ${this.context}`;
			this.severities =
				this.enabledFilters.page === "inspection-history"
					? [
							{
								label: translateAndFormat("major defects", "title"),
								value: "255",
								icon: "assets/severity-major.svg",
							},
							{
								label: translateAndFormat("minor defects", "title"),
								value: "63,127",
								icon: "assets/severity-minor.svg",
							},
							{
								label: translateAndFormat("no defects", "title"),
								value: "0",
								icon: "assets/severity-none.svg",
							},
					  ]
					: [
							{
								label: translateAndFormat("major defects", "title"),
								value: "255",
								icon: "assets/severity-major.svg",
							},
							{
								label: translateAndFormat("minor defects", "title"),
								value: "63,127",
								icon: "assets/severity-minor.svg",
							},
					  ];
		}

		// inspection status filter init
		if (this.enabledFilters.inspectionStatus) {
			this.inspectionStatusContext = `inspection-status-filter: ${this.context}`;
			this.inspectionStatusOptions = [
				{
					label: translateAndFormat("incomplete", "capitalize"),
					value: "incomplete",
				},
				{
					label: translateAndFormat("unverified", "capitalize"),
					value: "unverified",
				},
			];
		}

		this.initState();

		// listen for requests to clear state
		this.subscriptions.add(
			this.memCacheService
				.cleared$()
				.pipe(filter(context => this.context === context))
				.subscribe(() => {
					this.memCacheService.setValue("resetState", true);
					this.clearContext();
					this.clearState();
				}),
		);
	}

	clearState() {
		this.filtersActive = false;

		this.memCacheService.setValue(this.stateKey, null);
		this.currentQueryStrings = {
			dates: [{ startTime: this.defaultStartDate, endTime: this.defaultEndDate }],
		};

		this.initState();
	}

	clearContext() {
		this.memCacheService.setValue(this.assetIdContext, null);
		this.memCacheService.setValue(this.assetTypeContext, null);
		this.memCacheService.setValue(this.inspectionTypeContext, null);
		this.memCacheService.setValue(this.homeLocationContext, null);
		this.memCacheService.setValue(this.tripDurationContext, null);
		this.memCacheService.setValue(this.distanceTraveledContext, null);
		this.memCacheService.setValue(this.inspectorContext, null);
		this.memCacheService.setValue(this.severityContext, null);
	}

	getDefaultState(): FilterCardState {
		let defaultState: FilterCardState = {
			currentQueryStrings: {
				dates: [{ startTime: this.defaultStartDate, endTime: this.defaultEndDate }],
			},
			tableFilterChips: [],
		};

		if (this.enabledFilters.defectType) {
			defaultState = {
				...defaultState,
				currentQueryStrings: {
					...defaultState.currentQueryStrings,
					statuses: ["open", "pending"],
				},
			};
		}

		return defaultState;
	}

	isDefaultState() {
		const defaultState = this.getDefaultState();

		const defaultCustomDateRangeFilter =
			this.currentQueryStrings.dates.length === 1 &&
			this.currentQueryStrings.dates[0].startTime.getTime() ===
				defaultState.currentQueryStrings.dates[0].startTime.getTime() &&
			defaultState.currentQueryStrings.dates[0].endTime.toDateString() ===
				this.currentQueryStrings.dates[0].endTime.toDateString();

		return (
			equal(defaultState.currentQueryStrings, this.currentQueryStrings) &&
			arraysEquivalent(defaultState.tableFilterChips, this.tableFilterChips) &&
			defaultCustomDateRangeFilter
		);
	}

	initState() {
		this.state = this.getState();

		if (!this.state) {
			this.state = this.getDefaultState();
			this.memCacheService.setValue("resetState", true);
		}

		if (
			JSON.stringify(this.currentQueryStrings, urlSearchParamsReplacer) !==
				JSON.stringify(this.state.currentQueryStrings, urlSearchParamsReplacer) ||
			this.memCacheService.getValue("isLoadingPage")
		) {
			this.currentQueryStrings = this.state.currentQueryStrings;
			this.tableFilterChips = this.state.tableFilterChips;

			this.getQueryStringsForTableFilterChips();

			this.emit();
		}
	}

	flushState() {
		this.memCacheService.setValue<FilterCardState>(this.stateKey, {
			currentQueryStrings: this.currentQueryStrings,
			tableFilterChips: this.tableFilterChips,
		});
	}

	emit(force = false) {
		this.flushState();

		if (
			(force ||
				!this.isDefaultState() ||
				this.memCacheService.getValue("resetState") ||
				this.memCacheService.getValue("isLoadingPage")) &&
			!this.resetCheckbox
		) {
			this.filterQueryObject.emit(this.currentQueryStrings);
		}

		this.filtersActive = !this.isDefaultState();
		this.memCacheService.setValue("resetState", false);
		this.memCacheService.setValue("isLoadingPage", false);
	}

	getState() {
		return this.memCacheService.getValue<FilterCardState>(this.stateKey);
	}

	// Get array of user selected defect types
	handleDefectTypeFilterEvent(event: DefectTypeOption) {
		this.addQueryString("statuses", event.value === "open" ? ["open", "pending"] : ["ignored", "repaired"]);
		this.emit();
	}

	// Get the user selected start and end dates from date filter and emit filter query
	public handleDateRangeFilterEvent(dateEvent: DateRangeFilterChange) {
		this.addQueryString("dates", dateEvent);
		if (!equal(this.getDefaultState().currentQueryStrings.dates, dateEvent))
			this.memCacheService.setValue("resetState", true);

		this.emit();
	}

	// Get an array of user selected inspections types.
	// Each selected value may be a comma-joined list of ids (grouped by stripped
	// label), so split + flatten to send every underlying id to the backend.
	handleInspectionTypeFilterEvent(inspectionTypes: Array<string>) {
		if (inspectionTypes.length) {
			this.addQueryString(
				"inspectionTypeIds",
				inspectionTypes.flatMap(item => item.split(",")),
			);
		} else {
			delete this.currentQueryStrings["inspectionTypeIds"];
		}

		this.emit();
	}

	// Get an array of user selected asset ids
	public handleAssetIdFilterEvent(assetIds: Array<string>) {
		if (assetIds.length) {
			this.addQueryString("assetIds", assetIds);
		} else {
			delete this.currentQueryStrings["assetIds"];
		}

		this.emit();
	}

	// Get an array of user selected asset types.
	// Each selected value may be a comma-joined list of ids (grouped by stripped
	// label), so split + flatten to send every underlying zone layout id to the backend.
	public handleAssetTypeFilterEvent(assetTypes: Array<string>) {
		if (assetTypes.length) {
			this.addQueryString(
				"zoneLayoutIds",
				assetTypes.flatMap(item => item.split(",")),
			);
		} else {
			delete this.currentQueryStrings["zoneLayoutIds"];
		}

		this.emit();
	}

	// Get an array of user selected asset ids
	public handleHomeLocationFilterEvent(homeLocations: Array<string>) {
		if (homeLocations.length) {
			this.addQueryString("divisionIds", homeLocations);
		} else {
			delete this.currentQueryStrings["divisionIds"];
		}

		this.emit();
	}

	// Get an array of user selected asset ids
	public handleInspectorFilterEvent(inspectors: Array<string>) {
		if (inspectors.length) {
			this.addQueryString("inspectorIds", inspectors);
		} else {
			delete this.currentQueryStrings["inspectorIds"];
		}

		this.emit();
	}

	// Get array of user selected severity types
	public handleSeverityFilterEvent(severities: Array<string>) {
		if (severities.length) {
			this.addQueryString("severities", severities.flatMap(item => item.split(",")).map(Number));
		} else {
			delete this.currentQueryStrings["severities"];
		}

		this.emit();
	}

	// Get an array of user selected inspection status values (e.g. "incomplete", "unverified")
	public handleInspectionStatusFilterEvent(inspectionStatuses: Array<string>) {
		if (inspectionStatuses.length) {
			this.addQueryString("inspectionStatus", inspectionStatuses);
		} else {
			delete this.currentQueryStrings["inspectionStatus"];
		}

		this.emit();
	}

	public handleTableFilterChips(tableFilterChip: TableFilterChip) {
		this.tableFilterChips = [...this.tableFilterChips, tableFilterChip];
		this.getQueryStringsForTableFilterChips();

		this.emit();
	}

	public getQueryStringsForTableFilterChips() {
		const isBigQuery = this.tableFilterChips[0]?.tableName.startsWith("bigQuery") ?? false;

		const output = [
			["assetId", "assets"],
			["inspectorId", "inspectors"],
			["divisionId", "divisions"],
		].reduce((acc, curr) => {
			const [id, tableName] = curr;
			const value = this.tableFilterChips
				.filter(chip =>
					isBigQuery
						? chip.tableName ===
						  `bigQuery${tableName.replace(/./, startLetter => startLetter.toLocaleUpperCase())}`
						: chip.tableName === tableName,
				)
				.map(chip => getIdFromRecord(chip.record, id));

			if (value && value.length) {
				acc = { ...acc, [`${id}s`]: value };
			}

			return acc;
		}, {});

		this.currentQueryStrings = {
			...this.currentQueryStrings,
			...output,
		};
	}

	public handleChipRemove(chip: TableFilterChip) {
		this.tableFilterChips = this.tableFilterChips.filter((tableFilterChip: TableFilterChip) => {
			return !tableFilterChipsEqual(tableFilterChip, chip);
		});

		this.getQueryStringsForTableFilterChips();

		const recordName = `${chip.tableQueryName.slice(0, -1)}Id`;
		const queryName = `${recordName}s`;
		const query = this.currentQueryStrings[queryName]?.filter(
			id => id !== getIdFromRecord(chip.record, recordName),
		);

		if (query?.length > 0) {
			this.addQueryString(queryName, query);
		} else {
			if (this.tableFilterChips.length === 0) {
				this.getTableFilterComponent.clearInput();
			}
			delete this.currentQueryStrings[queryName];
		}

		this.emit(true); // force, since we may be returning to the default state
	}

	clearAllState() {
		// signal child controls
		this.memCacheService.clear(this.context);
		if (this.enabledFilters.searchBar) {
			this.getTableFilterComponent.clearInput();
		}
	}

	/**
	 * Build the distance traveled options for the locale's unit system.
	 *
	 * Option values are always in meters, which is what the API expects, so only the label changes
	 * between systems. Keeping the conversion here means the label and the value cannot disagree.
	 */
	private buildDistanceOptions(): Array<SingleOption> {
		const isMetric = this.localeService.usesMetricSystem();
		const max = isMetric ? this.MAX_DISTANCE_TRAVEL_KILOMETERS : this.MAX_DISTANCE_TRAVEL_MILES;
		const metersPerUnit = isMetric ? METERS_PER_KILOMETER : METERS_PER_MILE;
		const singularKey = isMetric ? "kilometer or greater" : "mile or greater";
		const pluralKey = isMetric ? "kilometers or greater" : "miles or greater";

		return Array.from({ length: max }, (_, index) => {
			const magnitude = index + 1;
			return {
				value: Math.round(magnitude * metersPerUnit),
				label: `${magnitude} ${translateAndFormat(magnitude > 1 ? pluralKey : singularKey, "capitalize")}`,
			};
		});
	}

	// Get an array of user selected asset ids
	public handleDistanceFilterEvent(distanceInMeters: number) {
		if (distanceInMeters) {
			this.addQueryString("distance", distanceInMeters); // Options already carry meters
		} else {
			delete this.currentQueryStrings["distance"];
		}

		this.emit(true);
	}

	public handleTimeDurationEvent(timeDuration: number) {
		if (timeDuration) {
			this.addQueryString("duration", timeDuration * 60);
		} else {
			delete this.currentQueryStrings["duration"];
		}

		this.emit(true);
	}

	private addQueryString(key: string, value: any) {
		this.currentQueryStrings = {
			...this.currentQueryStrings,
			[key]: value,
		};
	}
}
